import { aiPerformanceMonitor } from './ai-performance-monitor'

interface Order {
  id: string
  serviceName: string
  serviceCategory: string
  clientId: string
  clientName: string
  providerId: string
  providerName: string
  price: number
  duration: number
  status: 'pending' | 'matched' | 'accepted' | 'in_progress' | 'completed' | 'cancelled' | 'reviewing'
  priority: 'low' | 'medium' | 'high' | 'urgent'
  location: string
  scheduledTime: string
  createdAt: number
  requirements: string[]
  tags: string[]
  clientRating?: number
  providerRating?: number
}

interface Provider {
  id: string
  name: string
  skills: string[]
  categories: string[]
  rating: number
  responseTime: number
  availability: number
  completedOrders: number
  cancellationRate: number
  location: string
  priceRange: { min: number; max: number }
}

interface MatchResult {
  orderId: string
  matchedProviderId: string
  matchScore: number
  confidence: number
  reasons: string[]
  alternatives: Array<{ providerId: string; score: number }>
}

interface OrderPrediction {
  orderId: string
  estimatedCompletionTime: string
  probabilityOfCompletion: number
  riskLevel: 'low' | 'medium' | 'high'
  riskFactors: string[]
  suggestions: string[]
}

export class AIOrderIntelligence {
  private orders: Map<string, Order> = new Map()
  private providers: Map<string, Provider> = new Map()
  private matchHistory: Map<string, MatchResult> = new Map()
  private predictionHistory: Map<string, OrderPrediction> = new Map()

  constructor() {
    this.initializeMockData()
  }

  private initializeMockData() {
    // 不再初始化模拟数据，保持空数据状态，由真实数据填充
  }

  async smartMatchOrder(orderId: string): Promise<MatchResult> {
    const order = this.orders.get(orderId)
    if (!order) {
      throw new Error('订单不存在')
    }

    const allProviders = Array.from(this.providers.values())
    const candidates = this.filterProviders(order, allProviders)
    const scoredProviders = this.scoreProviders(order, candidates)
    
    scoredProviders.sort((a, b) => b.score - a.score)

    const bestMatch = scoredProviders[0]
    const alternatives = scoredProviders.slice(1, 4).map(p => ({ providerId: p.provider.id, score: p.score }))

    const reasons = this.generateMatchReasons(order, bestMatch?.provider)
    
    const result: MatchResult = {
      orderId,
      matchedProviderId: bestMatch?.provider.id || '',
      matchScore: bestMatch?.score || 0,
      confidence: bestMatch ? Math.min(1, bestMatch.score * 0.8 + 0.2) : 0,
      reasons,
      alternatives
    }

    this.matchHistory.set(orderId, result)

    if (bestMatch) {
      order.providerId = bestMatch.provider.id
      order.providerName = bestMatch.provider.name
      order.status = 'matched'
    }

    aiPerformanceMonitor.recordMatchSuccess(orderId, bestMatch?.score || 0)

    return result
  }

  private filterProviders(order: Order, providers: Provider[]): Provider[] {
    return providers.filter(p => {
      if (!p.categories.includes(order.serviceCategory)) {
        return false
      }
      
      if (p.priceRange.min > order.price || p.priceRange.max < order.price) {
        return false
      }
      
      if (p.availability < 0.3) {
        return false
      }
      
      if (p.cancellationRate > 0.15) {
        return false
      }

      return true
    })
  }

  private scoreProviders(order: Order, providers: Provider[]): Array<{ provider: Provider; score: number }> {
    return providers.map(provider => {
      let score = 0

      if (provider.skills.includes(order.serviceName)) {
        score += 0.3
      }

      score += provider.rating * 0.25

      score += (1 - provider.responseTime / 60) * 0.15

      score += provider.availability * 0.1

      score += Math.min(1, provider.completedOrders / 100) * 0.1

      const locationBonus = this.calculateLocationScore(order.location, provider.location)
      score += locationBonus * 0.1

      return { provider, score: Math.round(score * 100) / 100 }
    })
  }

  private calculateLocationScore(orderLocation: string, providerLocation: string): number {
    const nearbyLocations: Record<string, string[]> = {
      '朝阳区': ['东城区', '海淀区'],
      '海淀区': ['朝阳区', '西城区'],
      '西城区': ['海淀区', '东城区'],
      '东城区': ['朝阳区', '西城区'],
      '丰台区': ['朝阳区', '东城区']
    }

    if (orderLocation === providerLocation) {
      return 1
    }

    if (nearbyLocations[orderLocation]?.includes(providerLocation)) {
      return 0.7
    }

    return 0.3
  }

  private generateMatchReasons(order: Order, provider: Provider | undefined): string[] {
    if (!provider) {
      return ['未找到合适的服务提供者']
    }

    const reasons: string[] = []

    if (provider.skills.includes(order.serviceName)) {
      reasons.push(`${provider.name}具备${order.serviceName}专业技能`)
    }

    if (provider.rating >= 4.7) {
      reasons.push(`${provider.name}评分${provider.rating}，口碑优秀`)
    }

    if (provider.responseTime <= 15) {
      reasons.push(`${provider.name}响应时间快（${provider.responseTime}分钟）`)
    }

    if (provider.location === order.location) {
      reasons.push(`${provider.name}与您在同一区域（${provider.location}）`)
    }

    if (provider.completedOrders >= 100) {
      reasons.push(`${provider.name}经验丰富，已完成${provider.completedOrders}单`)
    }

    if (reasons.length === 0) {
      reasons.push(`${provider.name}是最匹配您需求的服务提供者`)
    }

    return reasons
  }

  async getOrderPrediction(orderId: string): Promise<OrderPrediction> {
    const order = this.orders.get(orderId)
    if (!order) {
      throw new Error('订单不存在')
    }

    const provider = this.providers.get(order.providerId)
    const factors = this.analyzeOrderRisk(order, provider)

    let riskLevel: 'low' | 'medium' | 'high' = 'low'
    if (factors.riskScore > 0.6) {
      riskLevel = 'high'
    } else if (factors.riskScore > 0.3) {
      riskLevel = 'medium'
    }

    const prediction: OrderPrediction = {
      orderId,
      estimatedCompletionTime: this.estimateCompletionTime(order),
      probabilityOfCompletion: Math.max(0.6, 1 - factors.riskScore),
      riskLevel,
      riskFactors: factors.riskFactors,
      suggestions: factors.suggestions
    }

    this.predictionHistory.set(orderId, prediction)

    return prediction
  }

  private analyzeOrderRisk(order: Order, provider: Provider | undefined): { riskScore: number; riskFactors: string[]; suggestions: string[] } {
    let riskScore = 0
    const riskFactors: string[] = []
    const suggestions: string[] = []

    if (!provider) {
      riskScore += 0.3
      riskFactors.push('未匹配服务提供者')
      suggestions.push('尽快完成订单匹配')
    } else {
      if (provider.cancellationRate > 0.1) {
        riskScore += 0.15
        riskFactors.push(`服务提供者取消率较高（${provider.cancellationRate * 100}%）`)
        suggestions.push('考虑备选服务提供者')
      }

      if (provider.responseTime > 30) {
        riskScore += 0.1
        riskFactors.push(`服务提供者响应较慢（${provider.responseTime}分钟）`)
        suggestions.push('提前与服务提供者确认')
      }

      if (provider.availability < 0.5) {
        riskScore += 0.15
        riskFactors.push('服务提供者可用时间较少')
        suggestions.push('确认服务提供者时间安排')
      }
    }

    if (order.priority === 'urgent') {
      riskScore += 0.1
      riskFactors.push('订单优先级高，时间紧迫')
      suggestions.push('优先处理该订单')
    }

    if (order.requirements.length > 3) {
      riskScore += 0.1
      riskFactors.push('需求较为复杂')
      suggestions.push('确认服务提供者能否满足所有需求')
    }

    if (order.price < 100) {
      riskScore += 0.1
      riskFactors.push('订单金额较低，可能影响服务质量')
      suggestions.push('考虑适当提高预算')
    }

    return {
      riskScore: Math.min(1, riskScore),
      riskFactors,
      suggestions
    }
  }

  private estimateCompletionTime(order: Order): string {
    const baseDuration = order.duration
    
    let adjustment = 0
    if (order.priority === 'urgent') {
      adjustment = -15
    } else if (order.priority === 'low') {
      adjustment = 15
    }

    if (order.requirements.length > 2) {
      adjustment += 20
    }

    const estimatedMinutes = baseDuration + adjustment
    const hours = Math.floor(estimatedMinutes / 60)
    const minutes = estimatedMinutes % 60

    if (hours > 0) {
      return `${hours}小时${minutes}分钟`
    }
    return `${minutes}分钟`
  }

  async getPendingOrdersWithSmartSorting(): Promise<Order[]> {
    const pendingOrders = Array.from(this.orders.values()).filter(o => o.status === 'pending')
    
    return pendingOrders
      .map(order => {
        let score = 0
        
        const priorityScore = { urgent: 0.4, high: 0.3, medium: 0.2, low: 0.1 }
        score += priorityScore[order.priority]
        
        const timeAgo = Date.now() - order.createdAt
        score += Math.max(0, 0.3 - timeAgo / 3600000)
        
        score += order.price / 500 * 0.2
        
        if (order.requirements.length > 0) {
          score += 0.1
        }
        
        return { order, score }
      })
      .sort((a, b) => b.score - a.score)
      .map(item => item.order)
  }

  async getProviderRanking(category?: string): Promise<Provider[]> {
    let providers = Array.from(this.providers.values())
    
    if (category) {
      providers = providers.filter(p => p.categories.includes(category))
    }
    
    return providers
      .map(p => {
        let score = 0
        
        score += p.rating * 0.3
        
        score += (1 - p.responseTime / 60) * 0.25
        
        score += p.availability * 0.2
        
        score += Math.min(1, p.completedOrders / 200) * 0.15
        
        score += (1 - p.cancellationRate) * 0.1
        
        return { provider: p, score }
      })
      .sort((a, b) => b.score - a.score)
      .map(item => item.provider)
  }

  async updateOrderStatus(orderId: string, status: Order['status']) {
    const order = this.orders.get(orderId)
    if (!order) {
      throw new Error('订单不存在')
    }

    const previousStatus = order.status
    order.status = status

    if (status === 'completed') {
      const provider = this.providers.get(order.providerId)
      if (provider) {
        provider.completedOrders++
      }
    }

    if (status === 'cancelled' && previousStatus !== 'completed') {
      const provider = this.providers.get(order.providerId)
      if (provider) {
        provider.cancellationRate = (provider.cancellationRate * provider.completedOrders + 1) / (provider.completedOrders + 1)
      }
    }

    aiPerformanceMonitor.recordOrderStatusChange(orderId, previousStatus, status)
  }

  addOrder(order: Omit<Order, 'id' | 'createdAt'>): Order {
    const newOrder: Order = {
      ...order,
      id: `order_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      createdAt: Date.now()
    }
    this.orders.set(newOrder.id, newOrder)
    return newOrder
  }

  addProvider(provider: Omit<Provider, 'id'>): Provider {
    const newProvider: Provider = {
      ...provider,
      id: `provider_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    }
    this.providers.set(newProvider.id, newProvider)
    return newProvider
  }

  getOrder(orderId: string): Order | undefined {
    return this.orders.get(orderId)
  }

  getAllOrders(): Order[] {
    return Array.from(this.orders.values())
  }

  getAllProviders(): Provider[] {
    return Array.from(this.providers.values())
  }

  getStats() {
    const pendingOrders = Array.from(this.orders.values()).filter(o => o.status === 'pending').length
    const matchedOrders = Array.from(this.orders.values()).filter(o => o.status === 'matched').length
    const completedOrders = Array.from(this.orders.values()).filter(o => o.status === 'completed').length
    const cancelledOrders = Array.from(this.orders.values()).filter(o => o.status === 'cancelled').length

    return {
      totalOrders: this.orders.size,
      pendingOrders,
      matchedOrders,
      completedOrders,
      cancelledOrders,
      matchRate: this.orders.size > 0 ? ((this.orders.size - pendingOrders) / this.orders.size) * 100 : 0,
      completionRate: this.orders.size > 0 ? (completedOrders / this.orders.size) * 100 : 0,
      totalProviders: this.providers.size,
      averageProviderRating: this.providers.size > 0 
        ? Array.from(this.providers.values()).reduce((sum, p) => sum + p.rating, 0) / this.providers.size
        : 0
    }
  }
}

export const aiOrderIntelligence = new AIOrderIntelligence()